import { useEffect, useState } from "react";
import { environment } from "../../environment";

export interface CompanyProvisionAgentConfig {
  agentId: string | undefined;
  serviceAccountId: string | undefined;
  isConfigured: boolean;
  isLoading: boolean;
}

/**
 * The `company_shell_provision` agent id and its execution service-account id
 * are both single, statically-configured environment values
 * (`ONBOARDING_AGENT_ID` / `ONBOARDING_SERVICE_ACCOUNT_ID`)
 * — same static-config pattern as `src/shadow-runs/hooks/useShadowRunAgentId.ts`.
 * There is no dynamic agent or service-account discovery: `isConfigured` is
 * true only once BOTH ids are present, since an invocation needs both. Until
 * the deployment supplies them, this resolves to `isConfigured: false`
 * everywhere and callers surface a "not available" fallback.
 */
export function useCompanyProvisionAgentId(): CompanyProvisionAgentConfig {
  const [agentId, setAgentId] = useState<string | undefined>();
  const [serviceAccountId, setServiceAccountId] = useState<
    string | undefined
  >();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    environment()
      .then((env) => {
        if (cancelled) return;
        const configuredAgent = env.ONBOARDING_AGENT_ID.trim();
        const configuredServiceAccount =
          env.ONBOARDING_SERVICE_ACCOUNT_ID.trim();
        setAgentId(configuredAgent || undefined);
        setServiceAccountId(configuredServiceAccount || undefined);
      })
      .catch(() => {
        if (!cancelled) {
          setAgentId(undefined);
          setServiceAccountId(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    agentId,
    serviceAccountId,
    isConfigured: !!agentId && !!serviceAccountId,
    isLoading,
  };
}
